import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Shell } from "@/components/Shell";
import Leaderboard from "@/components/Leaderboard";
import {
  getTeam,
  getGameByCode,
  listTeamPurchases,
  buyTest,
  submitDiagnosis,
  getGame,
  listTeams,
  fmtMoney,
} from "@/lib/api";

export default function TeamRoom() {
  const { id } = useParams();
  const [team, setTeam] = useState(null);
  const [game, setGame] = useState(null);
  const [teams, setTeams] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [flash, setFlash] = useState(false);
  const [dx, setDx] = useState("");
  const [dxSaving, setDxSaving] = useState(false);
  const dxInitRef = useRef(false);
  const [filter, setFilter] = useState("All");

  const refresh = async () => {
    try {
      const t = await getTeam(id);
      setTeam(t);
      const g = await getGame(t.game_id);
      setGame(g);
      setPurchases(await listTeamPurchases(id));
      if (g.status === "reveal" || g.status === "closed") {
        setTeams(await listTeams(t.game_id));
      }
      // Only initialize dx from server once, so polling never overwrites typing
      if (!dxInitRef.current) {
        setDx(t.diagnosis_submitted || "");
        dxInitRef.current = true;
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    refresh();
    const int = setInterval(refresh, 3000);
    return () => clearInterval(int);
  }, [id]);

  if (!team || !game)
    return (
      <Shell>
        <div className="font-mono-data text-sm text-white/60">Loading team console…</div>
      </Shell>
    );

  const purchasedTestIds = new Set(purchases.map((p) => p.test_id));

  const doBuy = async (testId) => {
    try {
      await buyTest(team.id, testId);
      setFlash(true);
      setTimeout(() => setFlash(false), 500);
      toast.success("Test ordered");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const doSubmit = async () => {
    if (!dx.trim()) return;
    setDxSaving(true);
    try {
      await submitDiagnosis(team.id, dx.trim());
      toast.success("Diagnosis saved");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setDxSaving(false);
    }
  };

  const categories = ["All", ...new Set(game.tests.map((t) => t.category))];
  const filtered = game.tests.filter((t) => filter === "All" || t.category === filter);
  const pct = (team.budget_remaining / game.starting_budget) * 100;
  const tone = pct > 70 ? "var(--dxc-volt)" : pct > 30 ? "#fff" : "var(--dxc-signal)";

  return (
    <Shell
      right={
        <div className="flex gap-4 items-center">
          <div className="text-right">
            <div className="font-mono-data text-[10px] tracking-widest text-white/50 uppercase">
              Team
            </div>
            <div className="font-serif-display text-xl leading-none">{team.name}</div>
          </div>
        </div>
      }
    >
      {/* Budget bar */}
      <motion.div
        animate={flash ? { backgroundColor: ["rgba(255,51,51,0.35)", "rgba(0,0,0,0)"] } : {}}
        transition={{ duration: 0.6 }}
        className="dxc-card p-6 mb-8"
        data-testid="team-budget-bar"
      >
        <div className="flex flex-wrap justify-between items-end gap-4">
          <div>
            <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
              Remaining Budget
            </div>
            <div
              data-testid="team-budget-value"
              className="font-mono-data text-6xl md:text-7xl mt-2"
              style={{ color: tone }}
            >
              {fmtMoney(team.budget_remaining)}
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
              Spent
            </div>
            <div className="font-mono-data text-3xl text-[color:var(--dxc-signal)]">
              -{fmtMoney(game.starting_budget - team.budget_remaining)}
            </div>
            <div className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase mt-1">
              Of {fmtMoney(game.starting_budget)}
            </div>
          </div>
        </div>
        <div className="mt-4 h-1 bg-white/10 relative">
          <motion.div
            initial={false}
            animate={{ width: `${pct}%` }}
            transition={{ type: "spring", damping: 20 }}
            className="absolute top-0 left-0 h-full"
            style={{ background: tone }}
          />
        </div>
      </motion.div>

      {/* 3 columns: Case, Catalog, Receipt */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-white/15">
        {/* CASE */}
        <div className="lg:col-span-4 p-8 lg:border-r border-white/15" data-testid="team-case-column">
          <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
            Patient / Case
          </div>
          <h2 className="font-serif-display text-3xl mt-2 leading-snug">
            {game.case_chief_complaint || "Chief complaint pending"}
          </h2>

          <div className="mt-8 space-y-6 text-[15px]">
            <CaseBlock k="History" v={game.case_history} />
            <CaseBlock k="Vitals" v={game.case_vitals} mono />
            <CaseBlock k="Exam" v={game.case_physical_exam} />
          </div>

          <div className="mt-10 pt-6 border-t border-white/10">
            <div className="flex justify-between items-baseline">
              <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
                Final Diagnosis
              </div>
              {team.diagnosis_submitted && dx.trim() !== (team.diagnosis_submitted || "").trim() && (
                <div
                  data-testid="team-dx-dirty"
                  className="font-mono-data text-[10px] tracking-widest text-[color:var(--dxc-signal)] uppercase"
                >
                  ● Unsaved
                </div>
              )}
              {team.diagnosis_submitted && dx.trim() === (team.diagnosis_submitted || "").trim() && (
                <div
                  data-testid="team-dx-saved"
                  className="font-mono-data text-[10px] tracking-widest text-[color:var(--dxc-volt)] uppercase"
                >
                  ✓ Saved
                </div>
              )}
            </div>
            <textarea
              data-testid="team-dx-input"
              value={dx}
              onChange={(e) => setDx(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  e.preventDefault();
                  doSubmit();
                }
              }}
              placeholder="Your working diagnosis…"
              className="dxc-input min-h-[110px] mt-2 font-serif-display italic text-base"
              disabled={game.status === "closed"}
            />
            <button
              data-testid="team-dx-submit"
              onClick={doSubmit}
              disabled={
                dxSaving ||
                !dx.trim() ||
                game.status === "closed" ||
                dx.trim() === (team.diagnosis_submitted || "").trim()
              }
              className="dxc-btn dxc-btn-volt w-full mt-3 text-xs"
            >
              {dxSaving
                ? "Saving…"
                : team.diagnosis_submitted
                  ? dx.trim() === (team.diagnosis_submitted || "").trim()
                    ? "Diagnosis Saved"
                    : "Update Diagnosis"
                  : "Submit Diagnosis"}
            </button>
            <div className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase mt-2">
              ⌘/Ctrl + Enter to save
            </div>
            {team.diagnosis_submitted && (
              <div
                data-testid="team-dx-saved-at"
                className="font-mono-data text-[10px] text-[color:var(--dxc-volt)]/80 uppercase tracking-widest mt-2"
              >
                Last saved · {new Date(team.diagnosis_submitted_at).toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>

        {/* CATALOG */}
        <div className="lg:col-span-5 p-8 lg:border-r border-white/15" data-testid="team-catalog-column">
          <div className="flex justify-between items-baseline">
            <div>
              <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
                Order Panel
              </div>
              <h2 className="font-serif-display text-3xl mt-1">Available tests</h2>
            </div>
            <select
              data-testid="team-catalog-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="dxc-input w-auto py-1 text-xs font-mono-data"
            >
              {categories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="mt-6 max-h-[65vh] overflow-y-auto -mr-2 pr-2">
            {filtered.map((t) => {
              const bought = purchasedTestIds.has(t.id);
              const canAfford = team.budget_remaining >= t.cost;
              const closed = game.status === "closed";
              return (
                <div
                  key={t.id}
                  data-testid={`catalog-item-${t.id}`}
                  className="grid grid-cols-12 gap-3 items-center py-3 border-b border-white/8"
                >
                  <div className="col-span-7">
                    <div className="text-[15px]">{t.name}</div>
                    <div className="font-mono-data text-[10px] text-white/40 uppercase tracking-widest mt-0.5">
                      {t.category}
                    </div>
                  </div>
                  <div className="col-span-2 font-mono-data text-right text-sm">
                    {fmtMoney(t.cost)}
                  </div>
                  <div className="col-span-3">
                    <button
                      data-testid={`buy-${t.id}`}
                      onClick={() => doBuy(t.id)}
                      disabled={bought || !canAfford || closed}
                      className={`dxc-btn w-full text-[10px] py-2 ${
                        bought ? "!border-[color:var(--dxc-volt)] !text-[color:var(--dxc-volt)]" : ""
                      }`}
                    >
                      {bought ? "Ordered" : closed ? "Closed" : canAfford ? "Order" : "Over budget"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RECEIPT */}
        <div className="lg:col-span-3 p-8" data-testid="team-receipt-column">
          <div className="font-mono-data text-[10px] tracking-[0.3em] text-white/50 uppercase">
            Order History
          </div>
          <h2 className="font-serif-display text-3xl mt-1">Receipt</h2>

          {purchases.length === 0 ? (
            <div className="mt-6 text-white/40 font-mono-data text-xs uppercase tracking-widest">
              No orders yet
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              <AnimatePresence>
                {purchases.map((p) => (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border-b border-white/8 pb-3"
                  >
                    <div className="flex justify-between gap-3">
                      <div className="text-sm leading-tight">{p.test_name}</div>
                      <div className="font-mono-data text-[color:var(--dxc-signal)] text-sm">
                        -{fmtMoney(p.cost)}
                      </div>
                    </div>
                    <div className="font-mono-data text-[10px] text-white/40 uppercase tracking-widest mt-1">
                      {new Date(p.purchased_at).toLocaleTimeString()}
                    </div>
                    {p.result ? (
                      <div
                        data-testid={`result-${p.id}`}
                        className="mt-2 p-2 border border-[color:var(--dxc-volt)]/40 bg-[color:var(--dxc-volt)]/5 text-[12px] font-mono-data whitespace-pre-wrap"
                      >
                        {p.result}
                      </div>
                    ) : (
                      <div className="mt-2 font-mono-data text-[10px] text-white/40 uppercase tracking-widest">
                        Result pending — host writing up
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {game.status === "reveal" && (
        <>
          <div className="mt-8 dxc-card p-8" data-testid="team-reveal-panel">
            <div className="font-mono-data text-[10px] tracking-[0.3em] text-[color:var(--dxc-volt)] uppercase">
              Session Reveal
            </div>
            <h2 className="font-serif-display text-3xl mt-1">The answer</h2>
            <div className="mt-3 text-lg">
              Correct diagnosis:{" "}
              <span className="text-[color:var(--dxc-volt)] font-serif-display italic">
                {game.correct_diagnosis || "—"}
              </span>
            </div>
          </div>

          <div className="mt-6">
            <Leaderboard
              teams={teams}
              startingBudget={game.starting_budget}
              testId="team-leaderboard"
            />
          </div>
        </>
      )}
    </Shell>
  );
}

const CaseBlock = ({ k, v, mono }) => (
  <div>
    <div className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase">{k}</div>
    <div className={`mt-1 whitespace-pre-wrap leading-relaxed ${mono ? "font-mono-data text-sm" : "font-serif-display"}`}>
      {v || <span className="text-white/30">—</span>}
    </div>
  </div>
);
